import React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Send, Mail, Phone, MapPin } from 'lucide-react';
import FadeIn from '../../../components/animations/FadeIn';
import { contactService } from '../../../services/contactService';

const ContactSection = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      message: '',
      subject: 'Website Contact'
    }
  });

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        name: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
        subject: data.subject || 'Website Contact'
      };

      await contactService.submit(payload);
      toast.success("Message sent successfully! We'll get back to you soon.");
      reset();
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    }
  };

  // ✅ SET YOUR LEFT ILLUSTRATION PATH HERE (like screenshot)
  const ILLUSTRATION = '/assets/images/contact-illustration.png';

  return (
    <section
      id="contact"
      className="relative overflow-hidden py-20 sm:py-24"
      style={{
        background: 'linear-gradient(90deg, #1f685879 50%, #add3fa3b 50%)'
      }}
    >
      {/* ✅ Bottom 50% = FULL WHITE (no pink visible at bottom) */}
      {/* ✅ ONE BIG WAVE: bottom area fully white (no pink showing) */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 sm:h-[85%] pointer-events-none">
        <svg
          className="w-full h-full"
          viewBox="0 0 1440 420"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {/* This path fills the whole bottom container in WHITE,
              with a wave edge at the TOP */}
          <path
            fill="#ffffff"
            d="M0,160
               C240,60 520,90 720,160
               C980,250 1180,250 1440,140
               L1440,420 L0,420 Z"
          />
        </svg>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <FadeIn>
          <div className="text-center text-gray-700">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Have Some Question?
            </h2>
            <p className="mt-3 text-gray/80 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
              Thank you for your interest in our services. Please fill out the form below or e-mail us and we will get
              back to you promptly regarding your request.
            </p>
          </div>
        </FadeIn>

        {/* Main card */}
        <div className="mt-10 sm:mt-12 bg-gray-200 shadow-2xl border border-white/60 overflow-hidden">
          <div className="grid lg:grid-cols-2">
            {/* Left */}
            <FadeIn direction="left">
              <div className="p-8 sm:p-10 lg:p-12">
                {/* Illustration */}
                <div className="flex justify-center lg:justify-start">
                  <img
                    src={ILLUSTRATION}
                    alt="Contact illustration"
                    className="w-full max-w-md object-contain"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>

                <div className="mt-8">
                  <h3 className="text-xl font-semibold text-gray-900">Get in touch</h3>

                  <div className="mt-6 space-y-5">
                    {/* Phone */}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <Phone size={18} className="text-gray-600" strokeWidth={2} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Phone</div>
                        <div className="text-sm text-gray-600">+91 9833916981</div>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <Mail size={18} className="text-gray-600" strokeWidth={2} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Email</div>
                        <div className="text-sm text-gray-600">sales@siwi.in</div>
                      </div>
                    </div>

                    {/* Address */}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <MapPin size={18} className="text-gray-600" strokeWidth={2} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Address</div>
                        <div className="text-sm text-gray-600">301,De-Elmas, Sonawala Road, Goregaon(East), Mumbai-400063</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Right form */}
            <FadeIn direction="right">
              <div className="p-8 sm:p-10 lg:p-12 bg-[#1f685879]">
                <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
                  <input type="hidden" {...register('subject')} />

                  <div className="space-y-4">
                    <div>
                      <input
                        {...register('firstName', { required: 'First name is required' })}
                        className="w-full h-11 px-4  border border-gray-200 bg-gray-50/70 focus:bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition"
                        placeholder="First Name *"
                      />
                      {errors.firstName && <div className="mt-1 text-xs text-red-500">{errors.firstName.message}</div>}
                    </div>

                    <div>
                      <input
                        {...register('lastName', { required: 'Last name is required' })}
                        className="w-full h-11 px-4  border border-gray-200 bg-gray-50/70 focus:bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition"
                        placeholder="Last Name *"
                      />
                      {errors.lastName && <div className="mt-1 text-xs text-red-500">{errors.lastName.message}</div>}
                    </div>

                    <div>
                      <input
                        {...register('phone')}
                        className="w-full h-11 px-4  border border-gray-200 bg-gray-50/70 focus:bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition"
                        placeholder="Phone"
                      />
                    </div>

                    <div>
                      <input
                        {...register('email', {
                          required: 'Email is required',
                          pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' }
                        })}
                        className="w-full h-11 px-4  border border-gray-200 bg-gray-50/70 focus:bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition"
                        placeholder="Email *"
                      />
                      {errors.email && <div className="mt-1 text-xs text-red-500">{errors.email.message}</div>}
                    </div>

                    <div>
                      <textarea
                        {...register('message', { required: 'Message is required' })}
                        rows={5}
                        className="w-full px-4 py-3 border border-gray-200 bg-gray-50/70 focus:bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition"
                        placeholder="Message *"
                      />
                      {errors.message && <div className="mt-1 text-xs text-red-500">{errors.message.message}</div>}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-11 font-semibold text-gray-700 flex items-center justify-center gap-2 disabled:opacity-60 transition"
                      style={{ background: 'linear-gradient(90deg, #b1cdd4fd 100%)' }}
                    >
                      <span>{isSubmitting ? 'Sending...' : 'SEND MESSAGE'}</span>
                      <Send size={18} />
                    </button>

                    <p className="text-[11px] text-gray-800 text-center leading-relaxed">
                      Your information is safe with us. We respect your privacy.
                    </p>
                  </div>
                </form>
              </div>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;